import type { Schemas } from "@norn/sdk"
import { FileInput } from "lucide-react"
import { useT } from "@/lib/i18n"
import { useFormatters } from "@/workspace/values"

type Evidence = Schemas["FieldEvidence"]

export interface Receipt {
  readonly system: string
  readonly record: string
  readonly mapping: string
  readonly transform: string | null
  readonly observedAt: string | null
  readonly recordedAt: string
  readonly commit: number
  readonly upstream: number
  readonly fields: string[]
}

/** Group per-field evidence by the source record and mapping that supplied it: one receipt each. */
export function receiptsOf(provenance: Schemas["ProvenanceResponse"] | undefined, version: number | undefined): Receipt[] {
  if (!provenance || (version !== undefined && provenance.version !== version)) return []
  const byKey = new Map<string, Receipt & { fields: string[] }>()
  for (const p of provenance.properties) {
    for (const e of p.evidence as Evidence[]) {
      const key = `${e.source.system}\u0000${e.source.record}\u0000${e.mappingVersion}\u0000${e.commit}`
      const r = byKey.get(key) ?? {
        system: e.source.system,
        record: e.source.record,
        mapping: e.mappingVersion,
        transform: e.transform,
        observedAt: e.observedAt,
        recordedAt: e.recordedAt,
        commit: e.commit,
        upstream: e.upstream.length,
        fields: [],
      }
      if (!r.fields.includes(p.property)) r.fields.push(p.property)
      byKey.set(key, r)
    }
  }
  return [...byKey.values()].sort((a, b) => b.commit - a.commit)
}

/** "From ERPNext · Sales Order SO-0001 · via mapping erpnext.sales_order@0.1.1 · observed …". */
export function ReceiptList({ receipts, labelOf }: { receipts: readonly Receipt[]; labelOf?: (field: string) => string }) {
  const t = useT()
  const fmt = useFormatters()
  return (
    <ul className="space-y-2">
      {receipts.map((r) => (
        <li key={`${r.system}/${r.record}/${r.mapping}/${r.commit}`} className="flex items-start gap-3 rounded-xl border border-dashed border-[var(--lineage)]/50 px-4 py-3">
          <FileInput className="mt-0.5 size-4 shrink-0 text-[var(--lineage)]" aria-hidden />
          <div className="min-w-0 space-y-1 text-sm">
            <p>
              {t("dv.receipt.from")} <span className="font-medium">{r.system}</span> ·{" "}
              <span dir="ltr" className="font-mono text-[0.8125rem]">
                {r.record}
              </span>
            </p>
            <p className="text-xs text-muted-foreground">
              {t("dv.receipt.via")}{" "}
              <span dir="ltr" className="font-mono">
                {r.mapping}
                {r.transform ? ` · ${r.transform}` : ""}
              </span>
              {r.observedAt ? ` · ${t("dv.receipt.observed", { when: fmt.date(r.observedAt) })}` : ""} · {t("dv.receipt.recorded", { when: fmt.date(r.recordedAt) })} ·{" "}
              {t("dv.receipt.commit", { commit: r.commit })}
              {r.upstream ? ` · ${t("objects.upstreamFacts", { count: r.upstream })}` : ""}
            </p>
            <p className="text-xs text-subtle-foreground">{t("dv.receipt.fields", { fields: r.fields.map((f) => labelOf?.(f) ?? f).join(", ") })}</p>
          </div>
        </li>
      ))}
    </ul>
  )
}
